import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const CompleteApplication = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [formData, setFormData] = useState({
    phone: '',
    address: '',
    role: 'admin', // Default role for applicants
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Update user document with complete information
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        phone: formData.phone,
        address: formData.address,
        role: 'pending', // Change to pending for admin review
        applicationStatus: 'pending_review',
        updatedAt: new Date().toISOString(),
      });

      setSuccess('Application submitted successfully! You will be notified once approved.');

      // Create notification for super admin
      const { query, collection, where, getDocs, setDoc } = await import('firebase/firestore');
      const superAdminsQuery = query(collection(db, 'users'), where('role', '==', 'superadmin'));
      const superAdminsSnapshot = await getDocs(superAdminsQuery);
      
      for (const superAdminDoc of superAdminsSnapshot.docs) {
        await setDoc(doc(db, 'notifications', `${superAdminDoc.id}_${currentUser.uid}_${Date.now()}`), {
          userId: superAdminDoc.id,
          applicantId: currentUser.uid,
          userEmail: currentUser.email,
          userName: currentUser.displayName || currentUser.email,
          type: 'new_admin_application',
          message: `New admin application from ${currentUser.displayName || currentUser.email} (${currentUser.email})`,
          read: false,
          createdAt: new Date().toISOString()
        });
      }

      // Auto logout after 3 seconds
      setTimeout(async () => {
        await logout();
        navigate('/auth/sign-in');
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting application:', error);
      setError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/sign-in');
  };

  if (!currentUser) {
    navigate('/auth/sign-in');
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            {/* Header with Logout */}
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Complete Your Application
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please provide additional information to complete your admin application
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                size="small"
              >
                Logout
              </Button>
            </Box>

            <Box sx={{ p: 4 }}>
              {/* User Info Card */}
              <Card
                sx={{
                  mb: 4,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.info.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonAddIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Account Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        <strong>Email:</strong> {currentUser.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonAddIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        <strong>Name:</strong> {currentUser.displayName || currentUser.email.split('@')[0]}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Application Form */}
              <form onSubmit={handleSubmit}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                
                {success && (
                  <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                    {success}
                  </Alert>
                )}

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Additional Information
                </Typography>

                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+1 234 567 8900"
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  multiline
                  rows={3}
                  placeholder="Your full address"
                  InputProps={{
                    startAdornment: <HomeIcon sx={{ mr: 1, color: 'text.secondary', mt: 1 }} />,
                  }}
                  sx={{ mb: 3 }}
                />

                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Your application will be reviewed by a super administrator.
                    You will receive an email notification once your account is approved.
                    Please ensure all information is accurate.
                  </Typography>
                </Alert>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={!loading && <SaveIcon />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit Application'}
                </Button>
              </form>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CompleteApplication;